/**
 * WorkOrdersManagerReportTab - live "what's open, what closed, what needs
 * attention" KPI view for Green Sheets (work orders). Shares its data with
 * the scheduled email digest — see jobs/workOrderManagerReport.js on the
 * backend (GET /api/work-orders/manager-report).
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Box, Card, Typography, Grid, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup, CircularProgress, Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import api from "../../utils/api";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

// comparison: { value, previous, delta, pctChange, higherIsBetter }. Colors
// green/red based on whether the direction of change is good for THIS
// metric (more Closed is good, more Overdue is bad) — gray when the metric
// has no inherent direction (New) or there's no prior period to compare to.
const TrendBadge = ({ comparison }) => {
  if (!comparison || comparison.delta === null || comparison.delta === undefined) {
    return <Typography variant="caption" sx={{ color: "#9aa5b4" }}>No prior data</Typography>;
  }
  const { delta, pctChange, higherIsBetter } = comparison;
  if (delta === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25 }}>
        <RemoveIcon sx={{ fontSize: 14, color: "#6b778c" }} />
        <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 700 }}>No change</Typography>
      </Box>
    );
  }
  const isGood = higherIsBetter === null ? null : (delta > 0) === higherIsBetter;
  const color = isGood === null ? "#6b778c" : (isGood ? "#16a34a" : "#dc2626");
  const Arrow = delta > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Tooltip title={`Was ${comparison.previous} last period`}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25 }}>
        <Arrow sx={{ fontSize: 14, color }} />
        <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
          {Math.abs(delta)} ({pctChange > 0 ? "+" : ""}{pctChange}%)
        </Typography>
      </Box>
    </Tooltip>
  );
};

const StatTile = ({ label, value, sublabel, accent, comparison }) => (
  <Card elevation={0} sx={{ ...adminCardSx, p: 3, textAlign: "center", height: "100%" }}>
    <Typography variant="overline" sx={{ letterSpacing: 1, color: "#6b778c", fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1, color: accent || "#223142", my: 0.75 }}>
      {value}
    </Typography>
    {sublabel && <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mb: comparison ? 0.5 : 0 }}>{sublabel}</Typography>}
    {comparison && <TrendBadge comparison={comparison} />}
  </Card>
);

const PERIOD_LABELS = { daily: "Today", weekly: "This Week", monthly: "This Month" };

// The five categories from the original ask. daysLabel lets "closed" show
// "Days to Resolve" instead of "Days Open" for the same underlying number
// (creation to resolution, rather than creation to now).
const SECTIONS = [
  { key: "awaitingResponse", title: "Awaiting Initial Response", subtitle: "No response yet, within the response window.", color: "#2f63ad", bg: "rgba(47,99,173,0.09)", daysLabel: "Days Open" },
  { key: "responseOverdue", title: "Approaching or Exceeding Response Deadline", subtitle: "No response yet, at or past the response window.", color: "#b45309", bg: "rgba(180,83,9,0.10)", daysLabel: "Days Open" },
  { key: "respondedOpen", title: "Responded, Still Open", subtitle: "A response was recorded, but the Green Sheet remains open.", color: "#2f63ad", bg: "rgba(47,99,173,0.09)", daysLabel: "Days Open" },
  { key: "escalation", title: "Escalation Required", subtitle: "Past the escalation window and still open.", color: "#dc2626", bg: "rgba(220,38,38,0.09)", daysLabel: "Days Open" },
  { key: "closed", title: "Formally Closed Out", subtitle: "Resolved during this period, for confirmation.", color: "#16a34a", bg: "rgba(22,163,74,0.09)", daysLabel: "Days to Resolve" },
];

const BucketSection = ({ section, bucket }) => {
  const hiddenCount = bucket ? bucket.count - bucket.items.length : 0;
  return (
    <Card elevation={0} sx={{ ...adminCardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", fontSize: "1.05rem" }}>
          {section.title}
        </Typography>
        <Chip size="small" label={bucket?.count ?? 0} sx={{ fontWeight: 700, fontSize: 12, bgcolor: section.bg, color: section.color }} />
      </Box>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
        {section.subtitle}
      </Typography>

      {bucket?.items.length ? (
        <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.08)", overflow: "hidden" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: "rgba(47,99,173,0.04)" }}>
              <TableRow>
                {["#", "Title", "Assigned To", section.daysLabel, "Status"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: "#223142", fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bucket.items.map(item => (
                <TableRow key={item.orderId} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Link to={`/home/workorders/${item.orderId}`} style={{ color: "#2f63ad", fontWeight: 700, textDecoration: "none" }}>
                      #{item.orderId}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Link to={`/home/workorders/${item.orderId}`} style={{ color: "#223142", textDecoration: "none" }}>
                      {item.title}
                    </Link>
                  </TableCell>
                  <TableCell>{item.assignedTo}</TableCell>
                  <TableCell>{item.daysOpen}</TableCell>
                  <TableCell>
                    <Chip size="small" label={item.status} sx={{ fontWeight: 600, fontSize: 11 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.08)", p: 2, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>None right now.</Typography>
        </Paper>
      )}
      {hiddenCount > 0 && (
        <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 1 }}>
          + {hiddenCount} more.{" "}
          <Link to="/home/workorders" style={{ color: "#2f63ad", fontWeight: 600, textDecoration: "none" }}>
            View all Work Orders
          </Link>
        </Typography>
      )}
    </Card>
  );
};

const WorkOrdersManagerReportTab = () => {
  const [period, setPeriod] = useState("weekly");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.get("/work-orders/manager-report", { params: { period: p } });
      setReport(res.data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(period); }, [load, period]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Open backlog, resolution activity, and what needs attention first, {PERIOD_LABELS[period].toLowerCase()}.
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2 } }}>
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading || !report ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatTile label="Open Now" value={report.openNow} comparison={report.comparisons?.openNow} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatTile label="New" value={report.newThisPeriod} sublabel={PERIOD_LABELS[period]} comparison={report.comparisons?.newThisPeriod} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatTile label="Closed" value={report.closedThisPeriod} sublabel={PERIOD_LABELS[period]} accent="#16a34a" comparison={report.comparisons?.closedThisPeriod} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatTile label="Overdue" value={report.overdueNow} sublabel="no response yet" accent={report.overdueNow > 0 ? "#b45309" : "#223142"} comparison={report.comparisons?.overdueNow} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatTile label="Escalated" value={report.escalatedNow} sublabel="needs action" accent={report.escalatedNow > 0 ? "#dc2626" : "#223142"} comparison={report.comparisons?.escalatedNow} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatTile
                label="Avg Days to Resolve"
                value={report.avgDaysToResolve ?? "N/A"}
                sublabel={`respond: ${report.avgDaysToRespond ?? "N/A"}`}
                comparison={report.comparisons?.avgDaysToResolve}
              />
            </Grid>
          </Grid>

          {SECTIONS.map(section => (
            <BucketSection key={section.key} section={section} bucket={report.buckets?.[section.key]} />
          ))}
        </>
      )}
    </Box>
  );
};

export default WorkOrdersManagerReportTab;
